import 'dayjs/locale/sv';

import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Download } from 'react-feather';

import { Link, Pdf, Typography, Wrapper } from '@/components';
import { BriefViewer } from '@/components/BriefViewer';
import { useAuthUser, useMobile } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { useBeat } from '@/hooks/client';

import styles from './CampaignPageBeat.module.css';

const getBeatColor = (date: Date) => {
  if (!date) {
    return 'var(--color-alert-warning)';
  }

  if (dayjs().isAfter(dayjs(date))) {
    return 'var(--color-alert-success-dark)';
  }

  return 'var(--color-alert-error)';
};

interface CampaignPageBeatProps {
  campaignId: string;
  beatId: string;
}

export function CampaignPageBeat({ campaignId, beatId }: CampaignPageBeatProps) {
  console.log(
    '🔍 [CampaignPageBeat] Component rendered with campaignId:',
    campaignId,
    'beatId:',
    beatId
  );
  const isMobile = useMobile();
  const user = useAuthUser();
  const { t } = useTranslation();

  const { data, isLoading } = useBeat(beatId);

  const memoBeat = useMemo(() => (data || {}) as Api.Beat, [data]);

  const memoBriefPdfs = memoBeat.brief?.files?.filter((file) => file.url.includes('.pdf')) || [];

  if (!user?.is_client) return null;

  const beatColor = getBeatColor(memoBeat?.date_start);

  return (
    <div className={styles.campaignPageBeat}>
      <Wrapper isLoading={isLoading} campaignId={campaignId}>
        {memoBeat && (
          <>
            <div className={styles.campaignPageBeatHeading}>
              <div
                className={styles.campaignPageBeatHeadingStatus}
                style={{ background: beatColor }}
              />

              <Typography sizes={['h3']}>{memoBeat.beat_name}</Typography>
            </div>

            <div
              style={{ background: '#fff', borderRadius: 'var(--border-radius)', padding: '1rem' }}
            >
              <div className={styles.campaignPageBeatData}>
                <Typography sizes={['h6']}>{t('campaignPageBeat.platform')}</Typography>

                {memoBeat.platform}
              </div>

              <div className={styles.campaignPageBeatData}>
                <Typography sizes={['h6']}>{t('campaignPageBeat.duration')}</Typography>

                {memoBeat.date_start
                  ? `${dayjs(memoBeat.date_start).format('YYYY-MM-DD')}${memoBeat.date_end ? ` - ${dayjs(memoBeat.date_end).format('YYYY-MM-DD')}` : ''}`
                  : '-'}
              </div>

              {memoBeat.product_delivery && (
                <div className={styles.campaignPageBeatData}>
                  <Typography sizes={['h6']}>{t('campaignPageBeat.deliveryMethod')}</Typography>

                  {memoBeat.product_delivery}
                </div>
              )}

              {memoBeat.products && !!Object.entries(memoBeat.products)?.length && (
                <div className={styles.campaignPageBeatDeliverables}>
                  {Object.entries(memoBeat.products)
                    .filter((product) => product[1] > 0)
                    .map((productItem) => (
                      <div
                        className={styles.campaignPageBeatDeliverablesItem}
                        key={`beat-product-${productItem}`}
                      >
                        <Typography sizes={['label-secondary']}>
                          {t(`translatedType.${(productItem[0] || '').toUpperCase()}`)}
                        </Typography>

                        <Typography sizes={['h1']}>{productItem[1]}</Typography>
                      </div>
                    ))}

                  {!!memoBriefPdfs.length &&
                    memoBriefPdfs.map((file) => (
                      <div
                        className={styles.campaignPageBeatDeliverablesItem}
                        key={`beat-file-${file.url}`}
                      >
                        <Typography sizes={['label-secondary']}>{file.filename}</Typography>

                        <Typography sizes={['h1']}>
                          <Link href={file.url} target="_blank">
                            <Download size={40} />
                          </Link>
                        </Typography>
                      </div>
                    ))}
                </div>
              )}

              {(memoBeat.campaign_brief?.description.trim() ||
                memoBeat.brief?.description.trim()) && (
                <div className={styles.campaignPageBeatBrief}>
                  <div className={styles.campaignPageBeatBriefHeading}>
                    <Typography sizes={['h6']}>{t('campaignPageBeat.headingBrief')}</Typography>
                  </div>

                  <BriefViewer
                    html={`${memoBeat.campaign_brief?.description ?? ''}${memoBeat.brief?.description ?? ''}`}
                  />
                </div>
              )}

              {!isMobile && !!memoBriefPdfs.length && (
                <div className={styles.campaignPageBeatFiles}>
                  {memoBriefPdfs.map((file) => (
                    <Pdf key={`campaign-page-beat-file-${file.url}`} url={file.url} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </Wrapper>

      {!isLoading && memoBeat && !!memoBeat.brief?.moodboard_urls?.length && (
        <>
          <div className={styles.campaignPageBeatMoodboardTitle}>
            <Typography sizes={['h5']}>{t('campaignPageBeat.headingMoodboard')}</Typography>
          </div>

          <div className={styles.campaignPageBeatMoodboardImages}>
            {memoBeat.brief?.moodboard_urls?.map((url) => (
              <div
                className={styles.campaignPageBeatMoodboardImage}
                style={{ backgroundImage: `url(${url})` }}
                key={`brief-image-${url}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
